import { isNegativeVeil, negativeVeilLabelPosition } from "./veilGeometry";

// The size and placement of every text box in the diagram, in ONE place.
//
// These numbers are consumed twice and must not diverge: once by Labels.jsx,
// which draws the plates and names, and once by labelObstacles(), which asks
// what a segment caption has to avoid. When the two disagreed — Labels.jsx held
// PLATE_W/PLATE_H and overlays.js held its own copies, plus a bare 2.1 for name
// width — the caption solver was clearing boxes that were not where it thought
// they were, which is how a "solved" caption still landed on a label.
//
// Widths are estimates on purpose, and deliberately GENEROUS: an undershooting
// estimate lets a caption clear a plate on paper and overlap it on screen.
// script/check_labels.mjs is what measures the real boxes, via getBBox.

// The knocked-out rectangle behind a path's Hebrew letter and number.
export const PLATE_W = 7.2;
export const PLATE_H = 5.8;

// Rough advance width per character for the name face at font-size 4.
export const NAME_CHAR_W = 2.1;
// The name box sits high on its baseline; these are its extents around it.
export const NAME_ASCENT = 3.4;
export const NAME_DESCENT = 1.2;

// Marker rules label to the right, inside the viewBox by this much.
export const MARKER_INSET = 7;
export const MARKER_CHAR_W = 1.2;
export const MARKER_ASCENT = 1.6;
export const MARKER_DESCENT = 1.4;

// The three negative veils label at a larger size than the horizontal rules —
// they are the headline of the lens, they sit in open space above the Tree, and
// at 2.7px they read as a footnote to a diagram they are supposed to precede.
// These MUST track .overlay__negative-veil-label in tree.css.
export const VEIL_CHAR_W = 2.0;
export const VEIL_ASCENT = 3.4;
export const VEIL_DESCENT = 1.2;

// Segment captions.
export const CAPTION_CHAR_W = 1.5;
export const CAPTION_H = 4;

// Outer-pillar names sat 11 units out, which widened the diagram and shrank the
// tree. 3 past the rim is as close in as they go: the selection ring is at
// radius + 2.6, and any less would touch the first letter.
export const SIDE_GAP = 3;
export const ABOVE_GAP = 3.5;
export const BELOW_GAP = 5.5;

// Where a sephirah's NAME sits. Placement is DATA — `label_position` on the
// row — not a rule in the component. The old rule ("middle pillar below, except
// the topmost") is what put Tiphareth's name on path plate 25.
export function nameAnchor(sephirah, radius) {
  const { x, y, labelPosition } = sephirah;
  switch (labelPosition) {
    case "above":
      return { x, y: y - radius - ABOVE_GAP, anchor: "middle" };
    case "below":
      return { x, y: y + radius + BELOW_GAP, anchor: "middle" };
    case "left":
      return { x: x - radius - SIDE_GAP, y: y + 1.3, anchor: "end" };
    default:
      return { x: x + radius + SIDE_GAP, y: y + 1.3, anchor: "start" };
  }
}

// The box a name occupies, given where it is anchored.
export function nameBox(sephirah, radius) {
  const place = nameAnchor(sephirah, radius);
  const width = sephirah.name.length * NAME_CHAR_W;
  const x1 =
    place.anchor === "end"
      ? place.x - width
      : place.anchor === "start"
        ? place.x
        : place.x - width / 2;
  return {
    x1,
    y1: place.y - NAME_ASCENT,
    x2: x1 + width,
    y2: place.y + NAME_DESCENT
  };
}

// The box a path's number/letter plate occupies.
export function plateBox(path) {
  return {
    x1: path.labelX - PLATE_W / 2,
    y1: path.labelY - PLATE_H / 2,
    x2: path.labelX + PLATE_W / 2,
    y2: path.labelY + PLATE_H / 2
  };
}

// Where a marker rule's label sits, and the box it occupies.
export function markerLabelX(viewBox) {
  return viewBox.x + viewBox.width - MARKER_INSET;
}

export function markerLabelPosition(group, viewBox) {
  if (isNegativeVeil(group)) {
    return negativeVeilLabelPosition(group, viewBox);
  }

  // A point marker labels BELOW its circle, like a sephirah whose name sits
  // under it — labelling it at the right-hand margin would put Da'ath's name a
  // long way from Da'ath.
  if (group.markerX !== null && group.markerX !== undefined) {
    return {
      x: group.markerX,
      y: group.markerY + (viewBox.radius || 7) + BELOW_GAP,
      anchor: "middle"
    };
  }

  return { x: markerLabelX(viewBox), y: group.markerY + 1, anchor: "end" };
}

export function markerBox(group, viewBox) {
  const position = markerLabelPosition(group, viewBox);
  const veil = isNegativeVeil(group);
  const point = group.markerX !== null && group.markerX !== undefined;
  const width = group.name.length * (veil || point ? VEIL_CHAR_W : MARKER_CHAR_W);
  const left = position.anchor === "middle" ? position.x - width / 2 : position.x - width;
  return {
    x1: left,
    y1: position.y - (veil || point ? VEIL_ASCENT : MARKER_ASCENT),
    x2: left + width,
    y2: position.y + (veil || point ? VEIL_DESCENT : MARKER_DESCENT)
  };
}
